import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import { ELSButton } from '@els/els-component-button-react';
import PageHeader from '../../components/page-header/PageHeader.component';
import { useHistory } from 'react-router';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import { ELSTooltip } from '@els/els-component-tooltip-react';
import { flowRight } from 'lodash';
import withHTMLHeadSEO from '../../hocs/with-html-head-seo/withHTMLHeadSEO.hoc';
import {
  EbookReadingResponseDto,
  RecommendationAttemptStatusDto,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { RoutePath } from '../../components/app/app.constants';
import { addSearchParams } from '../../utilities/app.utilities';
import withRecommendationPlayer, { RecommendationPlayerProps } from '../../hocs/with-recommendation-player/withRecommendationPlayer.hoc';
import {
  fetchEbookSectionSsoUrlAction,
  fetchEbookPageRangeSsoUrlAction,
} from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import { SherpathAIChatSidebar } from '../../components/sherpath-ai-chat-sidebar/SherpathAIChatSidebar.component';
import { SherpathAIChatToggle } from '../../components/sherpath-ai-chat-toggle/SherpathAIChatToggle.component';
import cx from 'classnames';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import {
  PageContextType,
  buildSherpathAIPageContext
} from '../../utilities/sherpath-ai-context.utilities';
import { getIsAIChatPageContextEnabled } from '../../components/app/app-state-selectors.utilities';
import { buildEbookReadingPageContext } from '../../utilities/sherpath-ai-page-context-builders.utilities';
import { getTotalProgress } from '../../utilities/remediation.utilities';
import {
  EolsUserHistoryDto,
  EolsUserHistoryResponseDto
} from '../../apis/eols-user-crud/eols-user-crud.dtos';
import {
  fetchUserHistoryByCourseByStateKey,
  postUserHistory
} from '../../apis/eols-user-crud/eols-user-crud.utilities';
import {
  ebookSecondsThreshold,
  ebookSecondsUserHistoryStateKey
} from './ebook-player.constants';
import {
  getEbookRecommendationData,
  getEbookRecommendationSubsection,
  getEbookTitleWithPagesDisplay,
} from '../../utilities/ebook.utilities';
import { RecordEmpty } from '../../models/common.models';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import withRemediationBase from '../../hocs/with-remediation-base/withRemediationBase.hoc';
import CloseRecommendationPlayerModal, { CloseRecommendationPlayerModalId } from '../../components/close-recommendation-player-modal/CloseRecommendationPlayerModal.component';
import FocusTrap from 'focus-trap-react';

export type EbookPlayerProps = RecommendationPlayerProps
  & ELSPropsFromModalService
  & ELSPropsFromToastService;

type EbookPlayerPageState = {
  ebookReadingResponseDto: EbookReadingResponseDto;
  usedSection: boolean;
  timeInSeconds: number;
  intervalId: NodeJS.Timeout;
  isTimerRunning: boolean;
  userHistoryDto: EolsUserHistoryResponseDto;
  isTabActive: boolean;
}

const defaultState: EbookPlayerPageState = {
  ebookReadingResponseDto: null,
  usedSection: false,
  timeInSeconds: 0,
  intervalId: null,
  isTimerRunning: false,
  userHistoryDto: null,
  isTabActive: true,
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const EbookPlayerPage = (props: EbookPlayerProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const [state, setState] = useState<EbookPlayerPageState>(defaultState);

  const getPostHistory = (
    seconds: number,
    userHistory: EolsUserHistoryResponseDto,
  ): EolsUserHistoryDto => {
    if (!userHistory && props.recommendationAttempt) {
      return {
        stateKey: ebookSecondsUserHistoryStateKey,
        stateInfo: seconds.toString(),
        courseSectionId: parseInt(appState.courseSectionId, 10),
        instanceId: props.recommendationAttempt.id.toString(),
        eolsUser: {
          id: parseInt(appState.userId, 10)
        }
      };
    }

    return {
      ...userHistory,
      stateInfo: seconds.toString(),
      eolsUser: {
        id: parseInt(appState.userId, 10)
      }
    };
  };

  const startTimer = (startTime: number) => {
    // Clear any existing interval first
    if (state.intervalId) {
      clearInterval(state.intervalId);
    }

    // Create new interval
    const id = setInterval(() => {
      setState((prevState) => {
        if (!prevState.isTabActive) {
          return prevState;
        }
        const timeInSeconds = prevState.timeInSeconds + 1;
        let isSaveTime = timeInSeconds >= 10 && timeInSeconds % 10 === 0;
        if (prevState.userHistoryDto && prevState.userHistoryDto.stateInfo === timeInSeconds.toString()) {
          isSaveTime = false;
        }

        if (isSaveTime) {
          postUserHistory(getPostHistory(timeInSeconds, prevState.userHistoryDto)).then((response: EolsUserHistoryResponseDto) => {
            setState((innerPrevState) => {
              return {
                ...innerPrevState,
                userHistoryDto: response
              };
            });
          });
        }

        return {
          ...prevState,
          timeInSeconds
        };
      });
    }, 1000);

    setState((prevState) => {
      return {
        ...prevState,
        timeInSeconds: startTime,
        isTimerRunning: true,
        intervalId: id
      };
    });
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      setState(prevState => ({
        ...prevState,
        isTabActive: isVisible
      }));
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (state.intervalId) {
        clearInterval(state.intervalId);
      }
    };
  }, [state.intervalId]);

  useEffect(() => {

    const { recommendation } = props;

    if (!recommendation) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      ebookReadingResponseDto: null,
      usedSection: false,
    }));

    const ebookData = getEbookRecommendationData(recommendation);
    const subSection = getEbookRecommendationSubsection(recommendation);
    const isCfiLinkingEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
      appState.featureFlagsGrouped,
      FEATURE_FLAG.ENABLE_EBOOK_CFI_LINKING,
      appState.courseSectionId
    );
    const sectionRequest = (isCfiLinkingEnabled && subSection.cfi)
      ? { isbn: ebookData.isbn, cfi: subSection.cfi }
      : null;
    const pageRangeRequest = {
      isbn: ebookData.isbn,
      startPage: subSection.page_range[0].toString(),
      endPage: subSection.page_range[1].toString()
    };

    const onError = () => {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: (
          <div>
            <p>eBook Reading Request Error</p>
            <p>Please refresh the page and try again. If the problem persists please contact your administrator.</p>
          </div>
        ),
        type: ELSWithToastService.types.NEGATIVE
      });
    };

    const applyResponse = (ebookReadingResponseDto: EbookReadingResponseDto, usedSection: boolean) => {
      setState((prevState) => ({
        ...prevState,
        ebookReadingResponseDto,
        usedSection,
      }));
    };

    if (sectionRequest) {
      fetchEbookSectionSsoUrlAction({
        appState,
        appStateRef,
        setAppState,
        sectionRequest,
        recommendationId: recommendation.id,
        recommendationAttemptId: props.recommendationAttempt?.id ?? null
      })
        .then((response) => applyResponse(response, true))
        .catch(() => {
          fetchEbookPageRangeSsoUrlAction({ appState, appStateRef, setAppState, pageRangeRequest })
            .then((response) => applyResponse(response, false))
            .catch(onError);
        });
    } else {
      fetchEbookPageRangeSsoUrlAction({ appState, appStateRef, setAppState, pageRangeRequest })
        .then((response) => applyResponse(response, false))
        .catch(onError);
    }
  }, [props.recommendation]);

  const history = useHistory();

  useEffect(() => {
    if (!props.recommendationAttempt) {
      return;
    }
    if (props.recommendationAttempt.status === RecommendationAttemptStatusDto.COMPLETED_PASSED) {
      return;
    }
    if (props.referer !== RoutePath.REMEDIATION_STUDENT_HOME) {
      return;
    }
    fetchUserHistoryByCourseByStateKey({
      userId: parseInt(appState.userId, 10),
      stateKey: ebookSecondsUserHistoryStateKey,
      courseSectionId: parseInt(appState.courseSectionId, 10),
      instanceId: props.recommendationAttempt.id.toString()
    }).catch((error) => {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw new Error(error);

    }).then((response) => {
      let starTime = 0;
      if (response?.length) {
        const [userHistory] = response;
        setState((prevState) => {
          return {
            ...prevState,
            userHistoryDto: userHistory
          };
        });
        starTime = parseInt(userHistory.stateInfo, 10);
      }
      startTimer(starTime);
    });
  }, [props.recommendationAttempt?.id]);

  // Build eBook-specific page context when data is available
  const getPageContext = () => {
    if (!props.recommendation || !appState.remediationBaseState.remediationAssignmentWithSources || !state.ebookReadingResponseDto) {
      return null;
    }

    if (!getIsAIChatPageContextEnabled(appState)) {
      return null;
    }

    // Build full context with eBook-specific data
    return buildSherpathAIPageContext({
      remediationAssignmentWithSources: appState.remediationBaseState.remediationAssignmentWithSources,
      assessmentMetrics: appState.remediationBaseState.assessmentMetrics,
      assessmentSources: appState.remediationBaseState.raasAssessmentSourceList,
      hesiExamInfo: appState.remediationBaseState.hesiExamInfo,
      totalProgressPercent: getTotalProgress(appState.remediationBaseState),
      pageType: PageContextType.EBOOK_READING,
      pageSpecificData: buildEbookReadingPageContext({
        recommendation: props.recommendation
      })
    });
  };

  const handleReturnNavigation = () => {
    if (props.referer === RoutePath.REMEDIATION_STUDENT_HOME) {
      return history.push(addSearchParams(RoutePath.REMEDIATION_POST_PLAYER, {
        recommendationAttemptId: props.recommendationAttempt.id
      }));
    }

    if (props.referer === RoutePath.REMEDIATION_STUDENT_DETAIL) {
      return history.push(RoutePath.REMEDIATION_STUDENT_DETAIL);
    }

    return null;
  };

  const showCompleteButton = () => {
    return props.referer === RoutePath.REMEDIATION_STUDENT_HOME
      && props.recommendationAttempt
      && props.recommendationAttempt.status !== RecommendationAttemptStatusDto.COMPLETED_PASSED;
  };

  const {
    recommendation,
    recommendationAttempt
  } = props;

  if (!recommendation || (!recommendationAttempt && props.referer === RoutePath.REMEDIATION_STUDENT_HOME)) {
    return null;
  }

  const subSection = getEbookRecommendationSubsection(recommendation);

  // Check feature flag to disable player timeout for QE testing
  const isTimeoutDisabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    appState.featureFlagsGrouped,
    FEATURE_FLAG.DISABLE_PLAYER_TIMEOUTS,
    appState.courseSectionId
  );
  const effectiveThreshold = isTimeoutDisabled ? 0 : ebookSecondsThreshold;
  const isCompleteBtnDisabled = state.timeInSeconds < effectiveThreshold;

  const getToolTipContent = () => {
    const minutes = Math.floor(state.timeInSeconds / 60);
    const seconds = state.timeInSeconds % 60;

    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    if (isCompleteBtnDisabled) {
      return (
        <>
          You can submit the reading after it has been open for one minute.
          Total reading time ({formattedMinutes}:{formattedSeconds}) will be reported to your instructor.
        </>
      );
    }
    return (
      <>
        Make sure you have completed the reading.
        Total reading time ({formattedMinutes}:{formattedSeconds}) will be reported to your instructor.
      </>
    );
  };

  const openPlayerModal = (isForSubmission: boolean, onConfirm: () => void) => {
    props.modalService.openCustomModal({
      modalId: CloseRecommendationPlayerModalId,
      noFocusTrap: true,
      modal: (
        <FocusTrap>
          <CloseRecommendationPlayerModal
              modalService={props.modalService}
              recommendationId={props.recommendation.id}
              recommendationFeedback={props.recommendationFeedback}
              recommendationContentType={props.recommendation.contentType}
              isForSubmission={isForSubmission}
              onConfirm={onConfirm}
          />
        </FocusTrap>
      ),
    });
  };

  const handleOpenCompleteModal = () => {
    if (state.timeInSeconds < effectiveThreshold) {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: <div>Cannot complete reading until at least a minute of read time</div>,
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }
    openPlayerModal(true, () => {
      props.handleSubmitAttempt(state.timeInSeconds).then(() => {
        clearInterval(state.intervalId);
        handleReturnNavigation();
      });
    });
  };

  return (
    <div className="c-scm-ebook-player">
      <div className="c-scm-ebook-player__header">
        <PageHeader
          title="eBook Excerpt"
          onCloseClick={() => openPlayerModal(false, handleReturnNavigation)}
        >
          <SherpathAIChatToggle
            remediationAssignmentWithSources={appState.remediationBaseState.remediationAssignmentWithSources}
            pageContextType={PageContextType.EBOOK_READING}
          />
        </PageHeader>
      </div>

      {
        state.ebookReadingResponseDto
        && subSection
        && (
          <>
            <div className="c-scm-ebook-player__main">
              <div className={cx('c-scm-ebook-player__content', {
                'c-scm-ebook-player__content--chat-active': appState.isChatActive
              })}>
                <FlexLayout classes={['c-scm-ebook-player__body']}>
                  <FlexItem modifiers={[FlexLayoutModifier.GROW]} classes={['c-scm-ebook-player__body-content']}>
                    <div className="c-scm-ebook-player__iframe-loading">
                      Loading eBook reading. Please allow a moment...
                    </div>
                    <iframe title="vital-source-reading-iframe"
                            src={state.ebookReadingResponseDto.signInUrl}
                            className="c-scm-ebook-player__iframe"
                            allowFullScreen
                            allow="clipboard-read; clipboard-write" />
                  </FlexItem>
                  <SherpathAIChatSidebar
                    remediationAssignmentWithSources={appState.remediationBaseState.remediationAssignmentWithSources}
                    pageContext={getPageContext()}
                    pageContextType={PageContextType.EBOOK_READING}
                  />
                </FlexLayout>
                <div className="c-scm-ebook-player__footer">
                  <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS_2X, FlexLayoutModifier.MIDDLE]}>
                    <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                      <div>
                        {
                          state.usedSection
                            ? subSection?.title && (
                              <h4 className="o-els-container o-els-container--1o4">
                                {subSection.title}
                              </h4>
                            )
                            : (
                              <div>
                                {getEbookTitleWithPagesDisplay(props.recommendation)}
                              </div>
                            )
                        }
                      </div>
                    </FlexItem>
                    <FlexItem isRender={showCompleteButton()}>
                      <ELSTooltip
                        showWhenHover
                        content={getToolTipContent()}
                      >
                        <ELSButton
                          isDisabled={isCompleteBtnDisabled}
                          onClick={handleOpenCompleteModal}
                          size="small"
                        >
                          Complete reading
                        </ELSButton>
                      </ELSTooltip>

                    </FlexItem>
                  </FlexLayout>
                </div>
              </div>
            </div>
          </>
        )
      }
    </div>
  );
};

const updateProgressState = false;

const enhance = flowRight(
  withHTMLHeadSEO({ title: 'Ebook Player' }),
  ELSWithToastService,
  ELSWithModalService,
  withRemediationBase(updateProgressState),
  withRecommendationPlayer,
);

const EbookPlayer = enhance(EbookPlayerPage) as unknown as React.FC<RecordEmpty>;

export default EbookPlayer;
